import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquarePlus, Check, Lock } from 'lucide-react';
import api from '../../../services/api';
import BaseModal from '../../../components/ui/BaseModal';

// « Demander plus de questions » (migration 248) : un abonné signale une catégorie, un sous-groupe ou un sujet
// à enrichir. Un non-abonné voit le bouton et apprend que c'est compris dans l'abonnement.
// Garde-fous côté serveur : une demande en cours par cible, 5 par mois.

// État de la thématique, chargé une fois par onglet Quiz : null tant que rien n'est connu ou hors thématique éditoriale.
export function useContentRequests(scope, enabled) {
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!enabled || !scope) return undefined;
        let cancelled = false;
        api.get(`/user/content-request.php?scope=${encodeURIComponent(scope)}`)
            .then(({ data }) => { if (!cancelled && data.data?.enabled) setState(data.data); })
            .catch(() => { /* silencieux : pas de bouton */ });
        return () => { cancelled = true; };
    }, [scope, enabled]);
    const markSent = useCallback((target, remaining) => {
        setState(s => s && { ...s, remaining, active: [...s.active, target] });
    }, []);
    return { state, markSent };
}

const sameTarget = (a, b) => a.matiere_id === b.matiere_id && (a.groupe || null) === (b.groupe || null)
    && (a.sous_sujet_id || null) === (b.sous_sujet_id || null);

export default function RequestMoreQuestions({ scope, requests, category, groupe = null, sujets = [] }) {
    const { state, markSent } = requests;
    const [open, setOpen] = useState(false);
    const [ssId, setSsId] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(false);
    if (!state) return null;

    const base = { matiere_id: category.id, groupe };
    const isActive = (id) => state.active.some(a => sameTarget(a, { ...base, sous_sujet_id: id }));
    const allSent = isActive(null);
    const cible = groupe || category.label;

    const close = () => { setOpen(false); setSent(false); setError(null); setMessage(''); setSsId(''); };
    const submit = async () => {
        setBusy(true);
        setError(null);
        const target = { ...base, sous_sujet_id: ssId ? Number(ssId) : null };
        try {
            const { data } = await api.post('/user/content-request.php', {
                scope, matiere_id: category.id, matiere_label: category.label, groupe,
                sous_sujet_id: target.sous_sujet_id, message,
            });
            markSent(target, data.data?.remaining ?? 0);
            setSent(true);
        } catch (e) {
            setError(e.response?.data?.message || 'La demande n’a pas pu être envoyée.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button type="button" className={`sl-request-more${allSent ? ' is-sent' : ''}`} onClick={() => setOpen(true)}>
                {allSent ? <Check size={14} /> : <MessageSquarePlus size={14} />}
                <span>{allSent ? 'Demande envoyée' : 'Demander plus de questions'}</span>
            </button>
            <BaseModal open={open} onClose={close} maxWidth="480px"
                title={`Plus de questions sur « ${cible} »`}
                icon={state.has_access ? <MessageSquarePlus size={18} /> : <Lock size={18} />}>
                {!state.has_access ? (
                    <div className="sl-request-modal">
                        <p>Avec l’abonnement, vous pouvez demander plus de questions sur une catégorie ou un sujet précis.</p>
                        <p>Les sujets les plus demandés sont enrichis en priorité, et vous êtes prévenu par mail quand les nouvelles questions sont en ligne.</p>
                        <Link to={`/compte/quiz/${scope}/abonnement`} className="btn btn-primary" onClick={close}>Voir l’abonnement</Link>
                    </div>
                ) : sent ? (
                    <div className="sl-request-modal">
                        <p><strong>Demande envoyée, merci.</strong></p>
                        <p>Vous recevrez un mail quand les nouvelles questions seront en ligne.</p>
                        <button type="button" className="btn btn-primary" onClick={close}>Fermer</button>
                    </div>
                ) : (
                    <div className="sl-request-modal">
                        <p>Dites-nous ce qui vous manque : les sujets les plus demandés sont enrichis en priorité.</p>
                        {sujets.length > 0 && (
                            <label className="sl-request-modal__field">
                                <span>Sujet</span>
                                <select value={ssId} onChange={(e) => setSsId(e.target.value)}>
                                    <option value="" disabled={allSent}>Tout « {cible} »{allSent ? ' (demande en cours)' : ''}</option>
                                    {sujets.map(ss => (
                                        <option key={ss.id} value={ss.id} disabled={isActive(ss.id)}>
                                            {ss.label}{isActive(ss.id) ? ' (demande en cours)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="sl-request-modal__field">
                            <span>Ce qui vous manque (facultatif)</span>
                            <textarea rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)}
                                placeholder="Notions, types de questions, niveau…" />
                        </label>
                        {error && <p className="sl-request-modal__error">{error}</p>}
                        <div className="sl-request-modal__actions">
                            <span className="sl-request-modal__quota">
                                {state.remaining} demande{state.remaining > 1 ? 's' : ''} restante{state.remaining > 1 ? 's' : ''} ce mois-ci
                            </span>
                            <button type="button" className="btn btn-primary" onClick={submit}
                                disabled={busy || state.remaining <= 0 || (!ssId && allSent)}>
                                {busy ? 'Envoi…' : 'Envoyer la demande'}
                            </button>
                        </div>
                    </div>
                )}
            </BaseModal>
        </>
    );
}
